import React, { useEffect, useLayoutEffect } from 'react';
import { useQueryClient } from "@tanstack/react-query"
import { findArray, filterArray } from 'helpers/utils'
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";
import { useDispatchLegends } from "components/common/legendContext";
import { useGetNocs, useGetSportCodes } from 'helpers/commonCodes';
import { usePositions } from './Positions';

export default function SportCode({ discipline, entity, code, abbr, isText, notFound, disableLegend, hideLegendMobile, legendFixed, expandedId }) {
    const { mirs_competition, mirs_language, mirs_discipline } = useUrlFriendlyToParameters()
    const queryClient = useQueryClient();
    const data = queryClient.getQueryData(["SportCodes", mirs_competition, mirs_language])?.sportCodes;
    const isAbbr = abbr || false // default value to false
    const _isText = isText || false // default value to false
    discipline = discipline || mirs_discipline

    const filter = {
        disciplineCode: [discipline],
        entityCode: [entity],
        sportCode: [code]
    }

    let spCode = getSpCode({ code: code, entity: entity, discipline: discipline })
    const description = findArray(data, filter, true).description;
    const descriptionDefault = notFound && !description ? notFound : description
    const dispatch = useDispatchLegends();
    useLayoutEffect(() => {
        if (!disableLegend && isAbbr && dispatch) {
            const payload = { id: spCode, name: description, hideLegendMobile: hideLegendMobile, legendFixed: legendFixed, expandedId: expandedId }   // hideLegendMobile ? { id: spCode, name: description, hideLegendMobile: true,legendFixed: legendFixed, expandedId: expandedId } : { id: spCode, name: description }
            dispatch({
                type: 'addlegend',
                payload: payload
            })
        }
    });

    if (_isText) {
        return (description)
    }

    return (
        <>
            {isAbbr
                ? <><abbr aria-hidden="true" title={description}>{spCode}</abbr> <span className="sr-only">{description}</span></>
                : descriptionDefault
            }
        </>
    );
}

export function getSportCodeObject(data, discipline, entity, code) {
    const filter = {
        disciplineCode: [discipline],
        entityCode: [entity],
        sportCode: [code]
    }
    let objSportCode = findArray(data, filter);
    return (
        objSportCode
    );
}

export function getSportCodeEntityObjects(data, discipline, entity) {
    const filter = {
        disciplineCode: [discipline],
        entityCode: [entity],
    }
    let objSportCode = filterArray(data, filter);
    return (
        objSportCode
    );
}

export function useSportCode() {
    const { mirs_discipline } = useUrlFriendlyToParameters()
    const data = useGetSportCodes();

    const dispatch = useDispatchLegends();


    return function (entity, code, disableLegend, hideLegendMobile, legendFixed, expandedId) {
        const filter = {
            disciplineCode: [mirs_discipline],
            entityCode: [entity],
            sportCode: [code]
        }
        let objSportCode = findArray(data.sportCodes, filter);
        if (!disableLegend && dispatch) {
            const payload = { id: objSportCode.sportCode, name: objSportCode.description, hideLegendMobile: hideLegendMobile, legendFixed: legendFixed, expandedId: expandedId }   // hideLegendMobile ? { id: spCode, name: description, hideLegendMobile: true,legendFixed: legendFixed, expandedId: expandedId } : { id: spCode, name: description }
            dispatch({
                type: 'addlegend',
                payload: payload
            })
        }

        return (
            objSportCode
        );
    }
}

const getSpCode = ({ code, entity, discipline }) => {
    let spCode = code;
    if (entity === '@AthleteStatus' && (discipline === 'FBL'|| discipline === 'FBB')) {
        switch (code) {
            case 'ABSENT':
                spCode = 'A'
                break;
            case 'BOOKED':
                spCode = 'X'
                break;
            case 'INJURED':
                spCode = 'I'
                break;
            case 'SUSPEND':
                spCode = 'N'
                break;
            default:
        }
    } else if (entity === '@AthleteStatus' && (discipline === 'HBL' || discipline === 'VVO')) {
        switch (code) {
            case 'INJURED':
                spCode = '(I)'
                break;
            case 'SUSPEND':
                spCode = '(S)'
                break;
            default:
        }
    }
    return spCode;

}

export function GGetSportsCode(discipline, note, entryEvent) {
    let obj = isValidNoteObj(note);
    if (obj?.prefix === 'CC') {
        //const positions = GetPositions();
        //let data = { 'code': entryEvent?.ee_value, 'description':positions(entryEvent?.ee_value)};
        let data = GetCommonCodefromNote(obj?.entityCode, entryEvent?.ee_value);
        return data;
    } else if (obj?.prefix === 'SC') {
        const sportCodes = GetSportsCodesList();
        // Apply filtering based on disciplineCode and entityCode
        const newSportCodes = sportCodes?.find(sc => sc?.disciplineCode === discipline
            && sc?.entityCode === obj?.entityCode
            && sc?.sportCode === entryEvent?.ee_value);
        return newSportCodes;
    } else {
        return;
    }
}

export const GetSportsCodesList = () => useGetSportCodes()?.sportCodes;


export function GetSportsCodefromNote(discipline, note, entryCode) {
    let entityCode = note?.split(" ").at(1);
    const sportCodes = useGetSportCodes()?.sportCodes;
    switch (entityCode) {
        case '@Hand':
            let data = getSportCodeEntityObjects(sportCodes, discipline, entityCode);
            data = data?.filter(d => d?.disciplineCode === discipline && d?.entityCode)?.find(es => es?.sportCode === entryCode)
            return data;
        case '@Position':
            const positions = GetPositions();
            let posData = { 'code': entryCode, 'description': positions(entryCode) };
            return posData;
        case '@Country':
            return GetNocs(entryCode);
        default:
            return;
    }
}

export function GetCommonCodefromNote(entityCode, entryCode) {
    switch (entityCode) {
        case '@Position':
            const positions = GetPositions();
            let posData = { 'code': entryCode, 'description': positions(entryCode) };
            return posData;
        case '@Country':
            return GetNocs(entryCode);
        default:
            return;
    }
}

export function GetPositions() {
    return usePositions();
}

export function GetNocs(code) {
    let params = useUrlFriendlyToParameters()
    const data = useGetNocs(params)
    const noc = data?.nocs?.find(n => n.code === code)
    return noc;
}


function isValidNote(str) {
    // Regular expression to match 'SC @' or 'CC @' followed by any word characters
    var regex = /(SC|CC)\s@(\w+)/;
    return regex.test(str);
}

function isValidNoteObj(str) {
    // Regular expression to match 'SC @' or 'CC @' followed by any word characters
    var regex = /(SC|CC)\s@(\w+)/;
    var match = regex.exec(str);
    if (match) {
        return {
            prefix: match[1],
            entityCode: '@' + match[2],
            isValid: true
        };
    } else {
        return {
            prefix: null,
            entityCode: null,
            isValid: false
        };
    }
}
